import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase";

/**
 * Fetches top-level approved submission folders for the given adminId.
 * Returns an array of folder objects with folderId, folderName, and other metadata.
 * @param {string} adminId
 */
export async function fetchApprovedFolders(adminId) {
  try {
    if (!adminId) {
      throw new Error("adminId is required to fetch approved folders");
    }
    const approvedFoldersRef = collection(
      db,
      "users",
      adminId,
      "approved_folders"
    );
    const querySnapshot = await getDocs(approvedFoldersRef);
    const folders = [];

    for (const docSnap of querySnapshot.docs) {
      folders.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    }
    return folders;
  } catch (error) {
    console.error("Error fetching approved folders:", error);
    return [];
  }
}

/**
 * Fetches subfolders for a given adminId and folderId.
 * Returns an array of subfolder objects with id and subfolderName.
 * @param {string} adminId
 * @param {string} folderId
 */
export async function fetchApprovedSubfolders(adminId, folderId) {
  try {
    if (!adminId || !folderId) {
      throw new Error(
        "[ERROR][ADMIN] fetchAppprovedSubfolder -> Missing adminId or folderId"
      );
    }
    const subfoldersRef = collection(
      db,
      "users",
      adminId,
      "approved_folders",
      folderId,
      "subfolders"
    );
    const querySnapshot = await getDocs(subfoldersRef);
    const subfolders = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        subfolderName: data.subfolderName || "Unnamed Subfolder",
      };
    });
    return subfolders;
  } catch (error) {
    console.error("Error fetching approved subfolders:", error);
    return [];
  }
}

/**
 * Fetches files for a given adminId, folderId and subfolderId.
 * Returns an array of file objects with id and file metadata.
 * @param {string} adminId
 * @param {string} folderId
 * @param {string} subfolderId
 */
export async function fetchApprovedFiles(adminId, folderId, subfolderId) {
  try {
    if (!adminId || !folderId || !subfolderId) {
      throw new Error("Missing adminId, folderId or subfolderId");
    }
    const filesRef = collection(
      db,
      "users",
      adminId,
      "approved_folders",
      folderId,
      "subfolders",
      subfolderId,
      "files"
    );
    const querySnapshot = await getDocs(filesRef);
    const files = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        fileName: data.fileName,
        downloadURL: data.downloadURL,
        storagePath: data.storagePath,
        fileType: data.fileType,
        fileSize: data.fileSize,
        folderId: data.folderId,
        source: data.source,
        summary: data.summary,
        classificationCategory: data.classificationCategory,
        classificationConfidence: data.classificationConfidence,
        createdAt: data.createdAt,
      };
    });
    return files;
  } catch (error) {
    console.error("Error fetching approved files:", error);
    return [];
  }
}
